import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Copy, KeyRound, Loader2, LogOut, MonitorSmartphone, Save, ShieldAlert, Trash2, UserPlus } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { del, get, patch, post } from "@/lib/api";
import { errorMessage } from "@/lib/errors";
import { formatDate, formatDateTime } from "@/lib/format";
import { COUNTRIES, CURRENCIES, countryName } from "@/lib/options";
import { SUPPORTED_LOCALES } from "@/i18n";
import { ROLES } from "@/lib/constants";
import { cn } from "@/lib/utils";
import { PageHeader } from "@/components/common/PageHeader";
import { Field, TextField } from "@/components/common/FormField";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

const TABS = ["organization", "members", "profile", "security"];

const Section = ({ title, hint, children, testId }) => (
  <section className="og-card" data-testid={testId}>
    <div className="border-b border-border px-5 py-4">
      <h2 className="font-heading text-base font-semibold">{title}</h2>
      {hint && <p className="text-sm text-muted-foreground">{hint}</p>}
    </div>
    <div className="p-5">{children}</div>
  </section>
);

/* ---------------- Organization ---------------- */
const OrganizationTab = () => {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const { orgId, currentOrg, hasRole, patchCurrentOrg } = useAuth();
  const isAdmin = hasRole("admin");
  const { data: org, isLoading } = useQuery({ queryKey: ["organization", orgId], queryFn: () => get("/organizations/current"), enabled: !!orgId });
  const [form, setForm] = useState(null);
  useEffect(() => {
    if (org) setForm({ name: org.name || "", default_locale: org.default_locale || "fr-CH", default_currency: org.default_currency || "CHF", country: org.country || "" });
  }, [org]);

  const save = useMutation({
    mutationFn: () => patch("/organizations/current", { ...form, country: form.country || null }),
    onSuccess: (saved) => {
      queryClient.setQueryData(["organization", orgId], (prev) => ({ ...(prev || {}), ...saved }));
      patchCurrentOrg({ name: saved.name, default_locale: saved.default_locale, default_currency: saved.default_currency });
      toast.success(t("settings.orgSaved"));
    },
    onError: (err) => toast.error(errorMessage(err)),
  });

  if (isLoading || !form) return <Skeleton className="h-64 w-full rounded-xl" />;
  const statusClass = { trial: "border-sky-200 bg-sky-50 text-sky-900", active: "border-emerald-200 bg-emerald-50 text-emerald-900", suspended: "border-rose-200 bg-rose-50 text-rose-900" }[org.status] || "border-border bg-muted";

  return (
    <div className="grid gap-4 lg:grid-cols-12">
      <div className="lg:col-span-7">
        <Section title={t("settings.tabs.organization")} hint={!isAdmin ? t("settings.adminOnly") : undefined} testId="settings-organization">
          <fieldset disabled={!isAdmin || save.isPending} className="space-y-4">
            <TextField id="org-name" label={t("settings.orgName")} value={form.name} onChange={(v) => setForm((f) => ({ ...f, name: v }))} testId="settings-org-name-input" />
            <div className="grid gap-4 sm:grid-cols-3">
              <Field id="org-locale" label={t("settings.defaultLocale")}>
                <Select value={form.default_locale} onValueChange={(v) => setForm((f) => ({ ...f, default_locale: v }))}>
                  <SelectTrigger id="org-locale" data-testid="settings-org-locale-select" className="bg-card"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-card">{SUPPORTED_LOCALES.map((l) => <SelectItem key={l.code} value={l.code}>{l.label}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <Field id="org-currency" label={t("settings.defaultCurrency")}>
                <Select value={form.default_currency} onValueChange={(v) => setForm((f) => ({ ...f, default_currency: v }))}>
                  <SelectTrigger id="org-currency" data-testid="settings-org-currency-select" className="bg-card"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-card">{CURRENCIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <Field id="org-country" label={t("settings.country")}>
                <Select value={form.country || ""} onValueChange={(v) => setForm((f) => ({ ...f, country: v }))}>
                  <SelectTrigger id="org-country" data-testid="settings-org-country-select" className="bg-card"><SelectValue placeholder={t("common.select")} /></SelectTrigger>
                  <SelectContent className="bg-card">{COUNTRIES.map((c) => <SelectItem key={c} value={c}>{countryName(c, i18n.language)}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
            </div>
            {isAdmin && (
              <div className="flex justify-end pt-2">
                <Button onClick={() => save.mutate()} disabled={save.isPending || form.name.trim().length < 2} data-testid="settings-org-save-button">{save.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}{t("common.save")}</Button>
              </div>
            )}
          </fieldset>
        </Section>
      </div>
      <div className="lg:col-span-5">
        <Section title={t("common.details")} testId="settings-organization-details">
          <dl className="space-y-3 text-sm">
            <div className="flex items-center justify-between gap-3"><dt className="text-muted-foreground">{t("settings.status")}</dt><dd><Badge variant="outline" className={statusClass} data-testid="settings-org-status">{t(`status.${org.status}`, { defaultValue: org.status })}</Badge></dd></div>
            <div className="flex items-center justify-between gap-3"><dt className="text-muted-foreground">{t("settings.plan")}</dt><dd className="font-medium" data-testid="settings-org-plan">{org.usage?.plan?.name || org.usage?.plan_code || "—"}</dd></div>
            <div className="flex items-center justify-between gap-3"><dt className="text-muted-foreground">{t("settings.yourRole")}</dt><dd className="font-medium">{t(`settings.roles.${org.role}`)}</dd></div>
            <div className="flex items-center justify-between gap-3"><dt className="text-muted-foreground">{t("settings.createdAt")}</dt><dd>{formatDate(org.created_at)}</dd></div>
            {org.trial_ends_at && <div className="flex items-center justify-between gap-3"><dt className="text-muted-foreground">{t("settings.trialEnds")}</dt><dd>{formatDate(org.trial_ends_at)}</dd></div>}
            <div className="flex items-center justify-between gap-3"><dt className="text-muted-foreground">Onboarding</dt><dd>{currentOrg?.onboarding_completed ? t("settings.onboardingDone") : t("settings.onboardingPending")}</dd></div>
            <div><dt className="text-muted-foreground">{t("settings.orgId")}</dt><dd className="mt-1 break-all rounded-md border border-border bg-muted px-2 py-1 font-mono text-xs" data-testid="settings-org-id">{org.id}</dd></div>
          </dl>
        </Section>
      </div>
    </div>
  );
};

/* ---------------- Members ---------------- */
const MembersTab = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { orgId, user, hasRole } = useAuth();
  const isAdmin = hasRole("admin");
  const { data: members, isLoading } = useQuery({ queryKey: ["members", orgId], queryFn: () => get("/organizations/current/members"), enabled: !!orgId });
  const [invite, setInvite] = useState({ email: "", full_name: "", role: "member" });
  const [invited, setInvited] = useState(null);

  const inviteMut = useMutation({
    mutationFn: () => post("/organizations/current/members", { email: invite.email.trim(), role: invite.role, full_name: invite.full_name.trim() || null }),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ["members", orgId] });
      setInvited(res);
      setInvite({ email: "", full_name: "", role: "member" });
      toast.success(t("settings.invited"));
    },
    onError: (err) => toast.error(errorMessage(err)),
  });
  const roleMut = useMutation({
    mutationFn: ({ id, role }) => patch(`/organizations/current/members/${id}`, { role }),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ["members", orgId] }); toast.success(t("settings.roleUpdated")); },
    onError: (err) => toast.error(errorMessage(err)),
  });
  const removeMut = useMutation({
    mutationFn: (id) => del(`/organizations/current/members/${id}`),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ["members", orgId] }); toast.success(t("settings.memberRemoved")); },
    onError: (err) => toast.error(errorMessage(err)),
  });

  const copyPwd = async () => {
    try { await navigator.clipboard.writeText(invited.temporary_password); toast.success(t("common.copied")); } catch (e) { /* noop */ }
  };

  return (
    <div className="space-y-4">
      <Section title={t("settings.tabs.members")} hint={t("settings.membersHint")} testId="settings-members">
        {isLoading ? <Skeleton className="h-40 w-full" /> : (
          <div className="overflow-x-auto">
            <Table data-testid="settings-members-table">
              <TableHeader className="og-table-head">
                <TableRow>
                  <TableHead>{t("common.name")}</TableHead>
                  <TableHead>{t("settings.role")}</TableHead>
                  <TableHead className="hidden md:table-cell">{t("settings.lastLogin")}</TableHead>
                  <TableHead className="hidden md:table-cell">{t("settings.joined")}</TableHead>
                  {isAdmin && <TableHead className="text-right">{t("common.actions")}</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {(members || []).map((m) => {
                  const isSelf = m.user_id === user?.id;
                  return (
                    <TableRow key={m.membership_id} className="hover:bg-muted/40" data-testid={`member-row-${m.membership_id}`}>
                      <TableCell>
                        <div className="font-medium">{m.full_name}{isSelf && <span className="ml-1.5 text-xs text-muted-foreground">({t("settings.you")})</span>}</div>
                        <div className="text-xs text-muted-foreground">{m.email}</div>
                      </TableCell>
                      <TableCell>
                        {isAdmin && !isSelf ? (
                          <Select value={m.role} onValueChange={(role) => roleMut.mutate({ id: m.membership_id, role })}>
                            <SelectTrigger className="h-8 w-[160px] bg-card" data-testid={`member-role-select-${m.membership_id}`}><SelectValue /></SelectTrigger>
                            <SelectContent className="bg-card">{ROLES.map((r) => <SelectItem key={r} value={r}>{t(`settings.roles.${r}`)}</SelectItem>)}</SelectContent>
                          </Select>
                        ) : (
                          <Badge variant="outline" className="border-border bg-muted">{t(`settings.roles.${m.role}`)}</Badge>
                        )}
                      </TableCell>
                      <TableCell className="hidden text-muted-foreground md:table-cell">{m.last_login_at ? formatDateTime(m.last_login_at) : "—"}</TableCell>
                      <TableCell className="hidden text-muted-foreground md:table-cell">{formatDate(m.joined_at)}</TableCell>
                      {isAdmin && (
                        <TableCell className="text-right">
                          {!isSelf && (
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <Button variant="ghost" size="sm" className="h-8 text-muted-foreground hover:text-rose-900" data-testid={`member-remove-${m.membership_id}`}><Trash2 className="mr-1.5 h-3.5 w-3.5" />{t("settings.removeMember")}</Button>
                              </AlertDialogTrigger>
                              <AlertDialogContent className="bg-card">
                                <AlertDialogHeader><AlertDialogTitle>{t("settings.removeMember")}</AlertDialogTitle><AlertDialogDescription>{t("settings.confirmRemoveMember", { name: m.full_name })}</AlertDialogDescription></AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel data-testid="member-remove-cancel">{t("common.cancel")}</AlertDialogCancel>
                                  <AlertDialogAction className="bg-destructive text-destructive-foreground hover:bg-destructive/90" onClick={() => removeMut.mutate(m.membership_id)} data-testid="member-remove-confirm">{t("common.confirm")}</AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          )}
                        </TableCell>
                      )}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}
      </Section>

      {isAdmin && (
        <Section title={t("settings.invite")} testId="settings-invite">
          {invited?.temporary_password && (
            <Alert className="mb-4 border-amber-200 bg-amber-50 text-amber-950" data-testid="settings-invite-temp-password">
              <KeyRound className="h-4 w-4" />
              <AlertTitle>{t("settings.tempPasswordTitle")} — {invited.email}</AlertTitle>
              <AlertDescription className="mt-1 space-y-2">
                <p className="text-xs">{t("settings.emailNotConnected")}</p>
                <div className="flex items-center gap-2">
                  <code className="rounded-md border border-amber-200 bg-white/70 px-2 py-1 font-mono text-sm" data-testid="settings-invite-temp-password-value">{invited.temporary_password}</code>
                  <Button type="button" size="sm" variant="outline" className="bg-card" onClick={copyPwd} data-testid="settings-invite-copy"><Copy className="mr-1.5 h-3.5 w-3.5" />{t("common.copy")}</Button>
                </div>
              </AlertDescription>
            </Alert>
          )}
          <form className="grid gap-4 sm:grid-cols-[1fr_1fr_180px_auto] sm:items-end" onSubmit={(e) => { e.preventDefault(); inviteMut.mutate(); }} data-testid="settings-invite-form">
            <TextField id="invite-email" label={t("settings.inviteEmail")} type="email" value={invite.email} onChange={(v) => setInvite((f) => ({ ...f, email: v }))} required testId="settings-invite-email-input" />
            <TextField id="invite-name" label={t("settings.memberName")} value={invite.full_name} onChange={(v) => setInvite((f) => ({ ...f, full_name: v }))} testId="settings-invite-name-input" />
            <Field id="invite-role" label={t("settings.role")}>
              <Select value={invite.role} onValueChange={(v) => setInvite((f) => ({ ...f, role: v }))}>
                <SelectTrigger id="invite-role" data-testid="settings-invite-role-select" className="bg-card"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-card">{ROLES.map((r) => <SelectItem key={r} value={r}>{t(`settings.roles.${r}`)}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Button type="submit" disabled={inviteMut.isPending || !invite.email} data-testid="settings-invite-submit">{inviteMut.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <UserPlus className="mr-2 h-4 w-4" />}{t("settings.invite")}</Button>
          </form>
          <div className="mt-4 grid gap-2 text-xs text-muted-foreground sm:grid-cols-2">
            {ROLES.map((r) => <div key={r}><span className="font-medium text-foreground">{t(`settings.roles.${r}`)}</span> — {t(`settings.roleHints.${r}`)}</div>)}
          </div>
        </Section>
      )}
    </div>
  );
};

/* ---------------- Profile ---------------- */
const ProfileTab = () => {
  const { t } = useTranslation();
  const { user, updateProfile } = useAuth();
  const [form, setForm] = useState({ full_name: user?.full_name || "", locale: user?.locale || "fr-CH" });
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (user) setForm({ full_name: user.full_name || "", locale: user.locale || "fr-CH" }); }, [user]);

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await updateProfile({ full_name: form.full_name.trim(), locale: form.locale });
      toast.success(t("settings.profileSaved"));
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Section title={t("settings.tabs.profile")} hint={t("settings.profileHint")} testId="settings-profile">
      <form onSubmit={save} className="max-w-xl space-y-4" data-testid="settings-profile-form">
        <TextField id="profile-name" label={t("settings.fullName")} value={form.full_name} onChange={(v) => setForm((f) => ({ ...f, full_name: v }))} required minLength={2} testId="settings-profile-name-input" />
        <Field id="profile-email" label={t("auth.email")}>
          <Input id="profile-email" value={user?.email || ""} disabled data-testid="settings-profile-email" />
        </Field>
        <Field id="profile-locale" label={t("settings.interfaceLanguage")}>
          <Select value={form.locale} onValueChange={(v) => setForm((f) => ({ ...f, locale: v }))}>
            <SelectTrigger id="profile-locale" data-testid="settings-language-select" className="bg-card"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-card">{SUPPORTED_LOCALES.map((l) => <SelectItem key={l.code} value={l.code}>{l.label}</SelectItem>)}</SelectContent>
          </Select>
        </Field>
        <div className="flex justify-end pt-2">
          <Button type="submit" disabled={busy || form.full_name.trim().length < 2} data-testid="settings-save-button">{busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}{t("common.save")}</Button>
        </div>
      </form>
    </Section>
  );
};

/* ---------------- Security ---------------- */
const strongEnough = (pwd) => pwd.length >= 8 && /[A-Za-z]/.test(pwd) && /\d/.test(pwd);

const SecurityTab = () => {
  const { t } = useTranslation();
  const { logout } = useAuth();
  const queryClient = useQueryClient();
  const [pwd, setPwd] = useState({ current: "", next: "", confirm: "" });
  const [pwdError, setPwdError] = useState(null);
  const { data: sessions, isLoading } = useQuery({ queryKey: ["sessions"], queryFn: () => get("/auth/sessions") });

  const change = useMutation({
    mutationFn: () => post("/auth/change-password", { current_password: pwd.current, new_password: pwd.next }),
    onSuccess: () => { setPwd({ current: "", next: "", confirm: "" }); toast.success(t("settings.passwordUpdated")); },
    onError: (err) => setPwdError(errorMessage(err)),
  });
  const logoutAll = useMutation({
    mutationFn: () => post("/auth/logout-all"),
    onSuccess: async () => { toast.info(t("settings.revokedAll")); await logout(); queryClient.clear(); },
    onError: (err) => toast.error(errorMessage(err)),
  });

  const submitPwd = (e) => {
    e.preventDefault();
    setPwdError(null);
    if (!strongEnough(pwd.next)) return setPwdError(t("auth.passwordRules"));
    if (pwd.next !== pwd.confirm) return setPwdError(t("auth.passwordMismatch"));
    return change.mutate();
  };

  return (
    <div className="grid gap-4 lg:grid-cols-12">
      <div className="lg:col-span-5">
        <Section title={t("auth.changePassword")} testId="settings-change-password">
          <form onSubmit={submitPwd} className="space-y-4" data-testid="settings-password-form">
            {pwdError && <Alert className="border-rose-200 bg-rose-50 text-rose-900" data-testid="settings-password-error"><AlertDescription>{pwdError}</AlertDescription></Alert>}
            <TextField id="pwd-current" label={t("auth.currentPassword")} type="password" autoComplete="current-password" value={pwd.current} onChange={(v) => setPwd((p) => ({ ...p, current: v }))} required testId="settings-current-password-input" />
            <TextField id="pwd-next" label={t("auth.newPassword")} type="password" autoComplete="new-password" hint={t("auth.passwordRules")} value={pwd.next} onChange={(v) => setPwd((p) => ({ ...p, next: v }))} required testId="settings-new-password-input" />
            <TextField id="pwd-confirm" label={t("auth.passwordConfirm")} type="password" autoComplete="new-password" value={pwd.confirm} onChange={(v) => setPwd((p) => ({ ...p, confirm: v }))} required testId="settings-confirm-password-input" />
            <div className="flex justify-end"><Button type="submit" disabled={change.isPending || !pwd.current || !pwd.next || !pwd.confirm} data-testid="settings-change-password-button">{change.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <KeyRound className="mr-2 h-4 w-4" />}{t("auth.changePassword")}</Button></div>
          </form>
        </Section>
      </div>
      <div className="lg:col-span-7">
        <Section title={t("auth.sessions")} hint={t("settings.sessionsHint")} testId="settings-sessions">
          {isLoading ? <Skeleton className="h-32 w-full" /> : (
            <div className="overflow-x-auto">
              <Table data-testid="settings-sessions-table">
                <TableHeader className="og-table-head">
                  <TableRow><TableHead>{t("settings.device")}</TableHead><TableHead className="hidden sm:table-cell">{t("settings.ip")}</TableHead><TableHead>{t("settings.lastUsed")}</TableHead><TableHead className="hidden md:table-cell">{t("settings.expires")}</TableHead></TableRow>
                </TableHeader>
                <TableBody>
                  {(sessions || []).map((s) => (
                    <TableRow key={s.id} className="hover:bg-muted/40" data-testid="session-row">
                      <TableCell><div className="flex items-center gap-2 text-sm"><MonitorSmartphone className="h-4 w-4 shrink-0 text-muted-foreground" /><span className="max-w-[260px] truncate" title={s.user_agent}>{s.user_agent || "—"}</span></div></TableCell>
                      <TableCell className="hidden font-mono text-xs text-muted-foreground sm:table-cell">{s.ip_address || "—"}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">{formatDateTime(s.last_used_at || s.created_at)}</TableCell>
                      <TableCell className="hidden text-sm text-muted-foreground md:table-cell">{formatDate(s.expires_at)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
          <div className="mt-4 flex items-center justify-between gap-3 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2">
            <div className="flex items-center gap-2 text-sm text-rose-900"><ShieldAlert className="h-4 w-4" />{t("auth.logoutAll")}</div>
            <AlertDialog>
              <AlertDialogTrigger asChild><Button variant="outline" size="sm" className="bg-card" data-testid="settings-logout-all-button"><LogOut className="mr-1.5 h-3.5 w-3.5" />{t("auth.logoutAll")}</Button></AlertDialogTrigger>
              <AlertDialogContent className="bg-card">
                <AlertDialogHeader><AlertDialogTitle>{t("auth.logoutAll")}</AlertDialogTitle><AlertDialogDescription>{t("settings.confirmLogoutAll")}</AlertDialogDescription></AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel data-testid="settings-logout-all-cancel">{t("common.cancel")}</AlertDialogCancel>
                  <AlertDialogAction className="bg-destructive text-destructive-foreground hover:bg-destructive/90" onClick={() => logoutAll.mutate()} data-testid="settings-logout-all-confirm">{t("common.confirm")}</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </Section>
      </div>
    </div>
  );
};

export default function SettingsPage() {
  const { t } = useTranslation();
  const [params, setParams] = useSearchParams();
  const tab = TABS.includes(params.get("tab")) ? params.get("tab") : "organization";

  return (
    <div className="space-y-6" data-testid="settings-page">
      <PageHeader title={t("settings.title")} subtitle={t("settings.subtitle")} testId="settings-header" />
      <Tabs value={tab} onValueChange={(v) => setParams({ tab: v }, { replace: true })}>
        <TabsList className="h-auto w-full justify-start gap-1 overflow-x-auto bg-muted/60 p-1" data-testid="settings-tabs">
          {TABS.map((k) => (
            <TabsTrigger key={k} value={k} className={cn("data-[state=active]:bg-card data-[state=active]:shadow-sm")} data-testid={`settings-tab-${k}`}>{t(`settings.tabs.${k === "members" ? "members" : k}`)}</TabsTrigger>
          ))}
        </TabsList>
        <TabsContent value="organization" className="mt-4"><OrganizationTab /></TabsContent>
        <TabsContent value="members" className="mt-4"><MembersTab /></TabsContent>
        <TabsContent value="profile" className="mt-4"><ProfileTab /></TabsContent>
        <TabsContent value="security" className="mt-4"><SecurityTab /></TabsContent>
      </Tabs>
    </div>
  );
}
